/* eslint-disable max-lines */
import {
  EyeIcon,
  NoSymbolIcon,
  PencilSquareIcon
} from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import usePageIndex from "../../../hooks/usePageIndex";
import { useSearch } from "../../../hooks/useSearch";
import { IBroadcastListItem } from "../../../interfaces/esim/BroadcastNotifications.types";
import {
  cancelBroadcast,
  getAllBroadcasts
} from "../../../services/BroadcastNotification.service";
import {
  BroadcastStatus,
  broadcastStatusColor,
  broadcastStatusLabel
} from "../../../utils/esim/broadcastStatus";
import { formatUtcDateTime } from "../../../utils/esim/format";
import DeleteModal from "../../ui/DeleteModal";
import IconBtn from "../../ui/IconBtn";
import Status from "../../ui/Status";
import Table, { Row } from "../../ui/table";
import Pagination from "../../ui/table/Pagination";

const columns = [
  "Emri",
  "Statusi",
  "Dërgimi",
  "Pajisjet",
  "Të dërguara",
  "Të pranuara",
  "Të dështuara",
  "Veprimet"
];

const crumbs = ["Esim", "Njoftimet"];

const BroadcastsList = () => {
  const { response, loading, handlePageChange, page, refetch } = useSearch(
    getAllBroadcasts,
    {
      withQuerySearch: true,
      querySelector: "searchKeyword"
    }
  );
  const { pageIndex } = usePageIndex();

  const handleCancel = (id: number) =>
    cancelBroadcast(id).then((res) => {
      refetch();
      return res;
    });

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 11rem)" }}>
      <Table
        titles={columns}
        loading={loading}
        className="overflow-x-auto"
        style="hover"
      >
        {(response?.data.result || []).map((row: IBroadcastListItem) => {
          const canEdit = row.status === BroadcastStatus.Scheduled;
          const canCancel =
            row.status === BroadcastStatus.Scheduled ||
            row.status === BroadcastStatus.Sending;
          return (
            <Row
              key={row.id}
              name={
                <Link
                  to={`/esim/broadcasts/${row.id}`}
                  state={{ page: pageIndex || page, crumbs }}
                  className="font-medium text-primary"
                >
                  {row.name}
                </Link>
              }
              status={
                <Status
                  type={broadcastStatusColor(row.status)}
                  name={broadcastStatusLabel(row.status)}
                />
              }
              schedule={formatUtcDateTime(row.scheduledAtUtc || row.startedAtUtc)}
              devices={<span className="tabular-nums">{row.totalDevices}</span>}
              sent={<span className="tabular-nums">{row.sentCount}</span>}
              delivered={
                <span className="tabular-nums">{row.deliveredCount}</span>
              }
              failed={<span className="tabular-nums">{row.failedCount}</span>}
              actions={
                <span className="flex items-center">
                  <Link
                    to={`/esim/broadcasts/${row.id}`}
                    state={{ page: pageIndex || page, crumbs }}
                    title="Shiko detajet"
                  >
                    <IconBtn as="span" className="w-6 h-6">
                      <EyeIcon className="w-4 h-4" />
                    </IconBtn>
                  </Link>
                  {canEdit && (
                    <Link
                      to={`/esim/broadcasts/edit/${row.id}`}
                      state={{ page: pageIndex || page, crumbs }}
                      title="Modifiko"
                    >
                      <IconBtn as="span" className="w-6 h-6">
                        <PencilSquareIcon className="w-4 h-4" />
                      </IconBtn>
                    </Link>
                  )}
                  {canCancel && (
                    <DeleteModal
                      handleDelete={() => handleCancel(row.id)}
                      params={row.id}
                      title="Konfirmo anulimin"
                      paragraph="Njoftimi nuk do të dërgohet më tej."
                      successMessage="Njoftimi u anulua"
                      errorMessage="Anulimi dështoi"
                      loadingMessage="Duke anuluar..."
                      indicator={
                        <span title="Anulo dërgimin">
                          <IconBtn className="w-6 h-6 text-neutral select-none">
                            <NoSymbolIcon className="w-4 h-4" />
                          </IconBtn>
                        </span>
                      }
                    />
                  )}
                </span>
              }
            />
          );
        })}
      </Table>
      <Pagination
        onPageChange={handlePageChange}
        totalCount={response?.data.totalCount || 0}
        length={response?.data?.result?.length}
        page={page}
        pageSize={10}
      />
    </div>
  );
};

export default BroadcastsList;
